// Search-state for the preset menus index route. The route in AppRouter.tsx
// owns the validateSearch schema; this hook is a typed surface over
// TanStack's useSearch / useNavigate so consumers don't touch the router
// primitives directly. Tab / sort / filter changes reset page so previous
// pagination state never bleeds into the new view.

import { useCallback, useMemo } from 'react';
import { useNavigate, useSearch } from '@tanstack/react-router';

import { DEFAULT_PAGE_SIZE } from '@components/DataTable/constants';

export type PresetMenuStatus = 'active' | 'inactive';
export type PresetMenuSortColumn = 'name' | 'format' | '';
export type PresetMenuSortDirection = 'asc' | 'desc' | '';

export interface PresetMenusSearchParams {
  status: PresetMenuStatus;
  q: string;
  page: number;
  perPage: number;
  sort: PresetMenuSortColumn;
  direction: PresetMenuSortDirection;
  ownerIds: number[];
}

export const PRESET_MENUS_SEARCH_DEFAULTS: PresetMenusSearchParams = {
  status: 'active',
  q: '',
  page: 1,
  perPage: DEFAULT_PAGE_SIZE,
  sort: '',
  direction: '',
  ownerIds: [],
};

const PRESET_MENUS_ROUTE = '/preset_menus' as const;

const VALID_STATUSES: ReadonlySet<PresetMenuStatus> = new Set(['active', 'inactive']);
const VALID_SORTS: ReadonlySet<PresetMenuSortColumn> = new Set(['name', 'format', '']);
const VALID_DIRECTIONS: ReadonlySet<'asc' | 'desc'> = new Set(['asc', 'desc']);

function toNumberArray(raw: unknown): number[] {
  if (Array.isArray(raw)) {
    return raw.map(Number).filter((n) => Number.isFinite(n) && n > 0);
  }
  if (typeof raw === 'string' && raw.length > 0) {
    return raw.split(',').map(Number).filter((n) => Number.isFinite(n) && n > 0);
  }
  return [];
}

// validateSearch handler — coerces unknown URL input into the typed shape.
export function parsePresetMenusSearch(raw: Record<string, unknown>): PresetMenusSearchParams {
  const status: PresetMenuStatus = VALID_STATUSES.has(raw.status as PresetMenuStatus)
    ? (raw.status as PresetMenuStatus)
    : PRESET_MENUS_SEARCH_DEFAULTS.status;
  const sort: PresetMenuSortColumn = VALID_SORTS.has(raw.sort as PresetMenuSortColumn)
    ? (raw.sort as PresetMenuSortColumn)
    : PRESET_MENUS_SEARCH_DEFAULTS.sort;
  const direction: PresetMenuSortDirection = VALID_DIRECTIONS.has(raw.direction as 'asc' | 'desc')
    ? (raw.direction as 'asc' | 'desc')
    : PRESET_MENUS_SEARCH_DEFAULTS.direction;
  const page = Number(raw.page);
  const perPage = Number(raw.perPage);
  return {
    status,
    q: typeof raw.q === 'string' ? raw.q : PRESET_MENUS_SEARCH_DEFAULTS.q,
    page: Number.isFinite(page) && page > 0 ? page : PRESET_MENUS_SEARCH_DEFAULTS.page,
    perPage:
      Number.isFinite(perPage) && perPage > 0 ? perPage : PRESET_MENUS_SEARCH_DEFAULTS.perPage,
    sort,
    direction,
    ownerIds: toNumberArray(raw.ownerIds),
  };
}

export function usePresetMenusRouterSearch() {
  const search = useSearch({ from: PRESET_MENUS_ROUTE });
  const tanNavigate = useNavigate({ from: PRESET_MENUS_ROUTE });

  const navigate = useCallback(
    (updates: Partial<PresetMenusSearchParams>) => {
      tanNavigate({
        search: (prev) => ({ ...prev, ...updates }),
        replace: true,
      });
    },
    [tanNavigate],
  );

  return useMemo(
    () => ({
      search,
      navigate,
      setStatus: (status: PresetMenuStatus) => navigate({ status, page: 1 }),
      setQuery: (q: string) => navigate({ q, page: 1 }),
      setPage: (page: number) => navigate({ page }),
      setPerPage: (perPage: number) => navigate({ perPage, page: 1 }),
      setSort: (sort: PresetMenuSortColumn, direction: PresetMenuSortDirection) =>
        navigate({ sort, direction, page: 1 }),
      setOwnerIds: (ownerIds: number[]) => navigate({ ownerIds, page: 1 }),
    }),
    [search, navigate],
  );
}
